/**
 * @file   文物易绘-移动版详情页
 * @create 2026/09/29
 * @description 手机访问 /ArchaeologyLineDrawing 时，public/index.html 会加 /mobile 前缀改写到本页。
 *              内容与 PC 版 pages/introduction/System/ArchaeologyLineDrawing.jsx 对齐
 *              （首屏 / 核心能力 / 线图生成 / 在线编辑 / 成果导出 / 归档管理 / 产品定价），按移动站版式重做：
 *              首屏 + 吸顶锚点 tab + 各能力区块 + 定价 + 页脚。
 *              首屏与定价区的「产品咨询」按钮统一弹出服务电话弹框（含一键复制号码）。
 *              注意：移动站未注册 /mobile/register 等路由，页内只允许出现已注册的移动端路由或外部地址，
 *              否则会落到未匹配路由（控制台兜底布局）引发跳转循环。
 */
import React, { useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { IntroHeader } from '../header';
import { Icon } from '../../introduction/System/ArchaeologyLineDrawing';
import archaeologyPaper from '../../../assets/home/archaeology-paper.png';
import workflowComparison from '../../../assets/home/workflow-comparison2.png';
import editingInterface from '../../../assets/home/editing-interface.jpg';
import exportLineDrawing from '../../../assets/home/export-line-drawing.png';
import archiveProjectList from '../../../assets/home/archive-project-list.png';
import wechatConsultQr from '../../../assets/home/wechat-consultation-qr.png';
import shuwenLogo from '../../../assets/home/shuwen-tech-white.png';
import heritageLogo from '../../../assets/home/zhejiang-ai-cultural-heritage-logo.jpg';
import './style.scss';

/** 锚点 tab（与下方区块 id 一一对应，顺序即滚动顺序） */
const TABS = [
    { id: 'item0', label: '核心能力' },
    { id: 'item1', label: '线图生成' },
    { id: 'item2', label: '在线编辑' },
    { id: 'item3', label: '成果导出' },
    { id: 'item4', label: '归档管理' },
    { id: 'item5', label: '产品定价' }
];

/** 核心能力四张卡（对齐 PC 版 CAPABILITIES） */
const CAPABILITIES = [
    { icon: 'lineGenerate', title: '线图生成', desc: '基于文物影像智能生成线图，支持多种文物类型与绘图风格' },
    { icon: 'lineEdit', title: '在线编辑', desc: '叠加原始影像，便捷灵活地完成线图矢量化编辑' },
    { icon: 'resultExport', title: '成果导出', desc: '支持多分辨率图片与矢量线图文件快速导出' },
    { icon: 'projectArchive', title: '归档管理', desc: '按项目自定义归档管理文物线图成果' }
];

/** 文物类型（对齐 PC 版 ARTIFACTS） */
const ARTIFACTS = [
    { icon: 'layers', title: '通用' },
    { icon: 'vessel', title: '陶器' },
    { icon: 'bronze', title: '铜器' },
    { icon: 'stone', title: '石器' },
    { icon: 'jade', title: '玉器' },
    { icon: 'bone', title: '骨器' }
];

/** 绘制风格（对齐 PC 版 DRAWING_STYLES） */
const DRAWING_STYLES = [
    { icon: 'pen', title: '标准', desc: '完整保留文物轮廓与主要纹饰，展示文物整体结构' },
    { icon: 'spark', title: '高清细节', desc: '最大化保留铭文、纹饰和细小边缘，还原文物细节' },
    { icon: 'vessel', title: '纯轮廓', desc: '仅保留外形轮廓，忽略内部碎线，突出形体结构' }
];

/** 在线编辑功能（对齐 PC 版 EDITING_FUNCTIONS） */
const EDITING_FUNCTIONS = [
    { icon: 'pen', title: '临摹描绘' },
    { icon: 'lineEdit', title: '线条编辑' },
    { icon: 'styleAdjust', title: '样式调整' },
    { icon: 'smooth', title: '平滑' },
    { icon: 'breakLine', title: '打断' },
    { icon: 'ruler', title: '尺寸标定' }
];

/** 个人版 / 标准版 / 高级版共用权益（对齐 PC 版 FULL_FEATURES） */
const FULL_FEATURES = ['线图管理', '平面图生成', '平剖图生成', '在线编辑', '标准 / 高清 PNG 导出', '矢量图导出'];

/** 产品定价（对齐 PC 版 PLANS：价格/档位/额度与协作账号等硬指标一致） */
const PLANS = [
    {
        name: '试用版', amount: '免费', unit: '', note: '购买普通加油包可升级个人版',
        facts: [
            { label: '额度', value: '20 次', suffix: '/单图' },
            { label: '协作账号', value: '1 个账号' },
            { label: '数据存储', value: '30 天' }
        ],
        features: ['线图管理', '平面图生成', '标准 PNG 导出']
    },
    {
        name: '个人版', amount: '¥300', unit: '元/年', note: '可购买普通加油包增加额度',
        facts: [
            { label: '额度', value: '100 次', suffix: '/单图' },
            { label: '协作账号', value: '1 个账号' },
            { label: '数据存储', value: '1 年' }
        ],
        features: FULL_FEATURES
    },
    {
        name: '机构标准版', amount: '¥2,800', unit: '元/年', note: '可购买高级加油包增加额度', featured: true,
        facts: [
            { label: '额度', value: '1,500 次', suffix: '/单图' },
            { label: '协作账号', value: '1 主账号 + 4 子账号' },
            { label: '数据存储', value: '1 年' }
        ],
        features: FULL_FEATURES
    },
    {
        name: '机构高级版', amount: '¥5,800', unit: '元/年', note: '可购买高级加油包增加额度',
        facts: [
            { label: '额度', value: '3,500 次', suffix: '/单图' },
            { label: '协作账号', value: '1 主账号 + 9 子账号' },
            { label: '数据存储', value: '1 年' }
        ],
        features: FULL_FEATURES
    }
];

/** 加油包（对齐 PC 版 ADDONS） */
const ADDONS = [
    { name: '普通加油包', times: '100', scope: '仅限于试用版和个人版', price: '300' },
    { name: '高级加油包', times: '300', scope: '仅限于机构标准版和机构高级版', price: '600' }
];

/** 咨询电话：首屏产品咨询弹框与页脚统一使用（与 PC 版口径一致） */
const CONSULT_PHONE = '18162722753';
const CONSULT_PHONE_TEXT = '181-6272-2753';
const CONSULT_PHONE_SPACED_TEXT = '181 6272 2753';
const CONSULT_WORK_TIME = '工作日 09:00-18:00';

/**
 * @description 文物易绘移动版详情页
 */
export const WapArchaeologyLineDrawing = () => {
    /** 当前高亮的 tab 下标 */
    const [activeTab, setActiveTab] = useState(0);
    /** 首屏元素（用于判断 tab 是否吸顶） */
    const bannerRef = useRef(null);
    /** 页面容器（吸顶样式挂在容器上的 .active 上，与其它移动端 System 页一致） */
    const containerRef = useRef(null);
    /** 产品咨询弹框开关 */
    const [consultOpen, setConsultOpen] = useState(false);

    /** 平滑滚动到锚点区块（吸顶 tab 高度按 4rem 预留） */
    const scrollToAnchor = (anchorId) => {
        const anchor = document.getElementById(anchorId);
        if (!anchor) {
            return;
        }
        const top = anchor.offsetTop - 40;
        window.scrollTo({ top: top, behavior: 'smooth' });
    };

    /**
     * 复制咨询电话
     * 说明：内网 http 访问下 navigator.clipboard 不可用（非安全上下文），退回临时 textarea + execCommand
     */
    const onCopyPhone = async () => {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(CONSULT_PHONE_TEXT);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = CONSULT_PHONE_TEXT;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            console.log('[WapArchaeologyLineDrawing] 复制咨询电话成功: ' + JSON.stringify({ phone: CONSULT_PHONE_TEXT }));
            message.success('号码已复制');
        } catch (error) {
            console.warn('[WapArchaeologyLineDrawing] 复制咨询电话失败: ' + JSON.stringify({ message: error && error.message }));
            message.warn('复制失败，请长按号码手动复制');
        }
    };

    useEffect(() => {
        const onScroll = () => {
            const scrollT = document.documentElement.scrollTop || window.pageYOffset || document.body.scrollTop;
            const banner = bannerRef.current;
            if (banner) {
                const reachTab = scrollT >= banner.offsetTop + banner.clientHeight - 40;
                containerRef.current?.classList.toggle('active', reachTab);
            }
            let current = 0;
            TABS.forEach((item, index) => {
                const section = document.getElementById(item.id);
                if (section && scrollT >= section.offsetTop - 40) {
                    current = index;
                }
            });
            setActiveTab(current);
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();
        return () => {
            window.removeEventListener('scroll', onScroll);
        };
    }, []);

    return (<>
        <IntroHeader />
        <div className='mobile-content archaeology' ref={containerRef}>
            {/* 首屏：文物线图底纹 + 标题 + 简介 + 免费试用 */}
            <div className='banner archaeology' ref={bannerRef}>
                <img className='banner-paper' src={archaeologyPaper} alt='' />
                <div className='content'>
                    <p className='big'>文物易绘</p>
                    <div className='cont'>
                        依托 AI 图像生成技术，由文物影像自动生成符合《田野考古制图》行业标准的专业文物线图，
                        一站式完成平面图、平剖图生成与在线优化编辑，支持出版级成果导出及归档管理。
                    </div>
                    <div className='btnbox'>
                        <div className='btn btn2' onClick={() => setConsultOpen(true)}>产品咨询</div>
                        <div className='btn btn1' onClick={() => scrollToAnchor('item5')}>查看定价</div>
                    </div>
                </div>
            </div>

            {/* 吸顶锚点 tab */}
            <div className='tab'>
                <ul>
                    {TABS.map((item, index) => (
                        <li key={item.id} className={activeTab === index ? 'active' : null} onClick={() => scrollToAnchor(item.id)}>
                            {item.label}
                        </li>
                    ))}
                </ul>
            </div>

            {/* 01 核心能力 */}
            <div className='capability' id='item0'>
                <p className='title'>核心能力</p>
                <div className='cap-grid'>
                    {CAPABILITIES.map(item => (
                        <div className='cap-card' key={item.title}>
                            <Icon type={item.icon} className='cap-icon' />
                            <p className='cap-title'>{item.title}</p>
                            <p className='cap-desc'>{item.desc}</p>
                        </div>
                    ))}
                </div>
                <p className='cap-tip'>一站式完成文物线图生成、编辑、导出与管理。</p>
            </div>

            {/* 02 线图生成 */}
            <div className='generation' id='item1'>
                <p className='title'>线图生成</p>
                <figure className='figure'>
                    <img src={workflowComparison} alt='青铜器文物影像与生成的文物线图对照' />
                    <figcaption>基于文物影像智能生成线图，支持多种类型文物与绘图风格选择</figcaption>
                </figure>
                <p className='sub-title'>文物类型</p>
                <div className='artifact-grid'>
                    {ARTIFACTS.map(item => (
                        <div className='artifact-item' key={item.title}>
                            <Icon type={item.icon} className='artifact-icon' />
                            <span>{item.title}</span>
                        </div>
                    ))}
                </div>
                <p className='sub-title'>绘制风格</p>
                <div className='style-list'>
                    {DRAWING_STYLES.map(item => (
                        <div className='style-item' key={item.title}>
                            <Icon type={item.icon} className='style-icon' />
                            <div className='style-copy'>
                                <p className='style-title'>{item.title}</p>
                                <p className='style-desc'>{item.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 03 在线编辑 */}
            <div className='editing' id='item2'>
                <p className='title'>在线编辑</p>
                <div className='edit-function-list'>
                    {EDITING_FUNCTIONS.map(item => (
                        <div className='edit-function-item' key={item.title}>
                            <Icon type={item.icon} className='edit-icon' />
                            <span>{item.title}</span>
                        </div>
                    ))}
                </div>
                <div className='edit-pair'>
                    <figure>
                        <img src={editingInterface} alt='在线编辑界面，展示线图编辑工具栏与编辑属性面板' />
                    </figure>
                </div>
                <p className='section-note'>叠加原始文物影像，提供便捷、灵活的文物线图矢量化编辑优化</p>
            </div>

            {/* 04 成果导出 */}
            <div className='result-export' id='item3'>
                <p className='title'>成果导出</p>
                <figure className='figure'>
                    <img src={exportLineDrawing} alt='文物线图' />
                    <figcaption>一份线图分别输出 PNG 图片与 SVG 矢量文件</figcaption>
                </figure>
                <div className='export-list'>
                    <div className='export-item'>
                        <Icon type='fileImage' className='export-icon' />
                        <div className='export-copy'>
                            <p className='export-title'>线图图片</p>
                            <p className='export-desc'>标准、高清线图图片快速导出</p>
                        </div>
                    </div>
                    <div className='export-item'>
                        <Icon type='fileVector' className='export-icon' />
                        <div className='export-copy'>
                            <p className='export-title'>矢量文件</p>
                            <p className='export-desc'>SVG 格式文件导出，便于第三方应用</p>
                        </div>
                    </div>
                </div>
                <p className='section-note'>支持多分辨率图片和矢量线图文件快速导出</p>
            </div>

            {/* 05 归档管理 */}
            <div className='archive' id='item4'>
                <p className='title'>归档管理</p>
                <div className='archive-project'>
                    <img className='archive-project-image' src={archiveProjectList} alt='归档管理界面：按项目归档文物影像与线图，支持分类检索与批量导出' />
                </div>
                <div className='archive-function-list'>
                    {[{ icon: 'projectArchive', title: '分类管理' }, { icon: 'search', title: '快速检索' }, { icon: 'resultExport', title: '批量导出' }].map(item => (
                        <div className='archive-function-item' key={item.title}>
                            <Icon type={item.icon} className='archive-function-icon' />
                            <span>{item.title}</span>
                        </div>
                    ))}
                </div>
                <p className='section-note'>提供文物线图按项目进行自定义归档管理</p>
            </div>

            {/* 06 产品定价 */}
            <div className='pricing' id='item5'>
                <p className='title'>产品定价</p>
                <div className='plan-list'>
                    {PLANS.map(plan => (
                        <div className={`plan-card${plan.featured ? ' featured' : ''}`} key={plan.name}>
                            <div className='plan-head'>
                                <p className='plan-name'>{plan.name}</p>
                                <p className='plan-amount'>{plan.amount}{plan.unit ? <small>{plan.unit}</small> : null}</p>
                            </div>
                            <p className='plan-note'>{plan.note}</p>
                            {/* 额度 / 协作账号 / 数据存储（与 PC 版一致） */}
                            <dl className='plan-facts'>
                                {plan.facts.map(fact => (
                                    <div key={fact.label}>
                                        <dt>{fact.label}</dt>
                                        <dd>{fact.value}{fact.suffix ? <small>{fact.suffix}</small> : null}</dd>
                                    </div>
                                ))}
                            </dl>
                            <div className='plan-divider'><span>包含权益</span></div>
                            <ul className='plan-features'>
                                {plan.features.map(text => <li key={text}>{text}</li>)}
                            </ul>
                        </div>
                    ))}
                </div>
                <div className='addon-list'>
                    {ADDONS.map(addon => (
                        <div className='addon-card' key={addon.name}>
                            <div className='addon-copy'>
                                <p className='addon-name'>{addon.name}</p>
                                <p className='addon-times'>{addon.times} 次/单图</p>
                                <p className='addon-scope'>{addon.scope}</p>
                            </div>
                            <p className='addon-price'><small>¥</small>{addon.price}元</p>
                        </div>
                    ))}
                </div>
                <div className='consult-btn' onClick={() => setConsultOpen(true)}>产品咨询</div>
            </div>

            {/* 页脚：产品咨询 + 联合发布 + 返回顶部（内容对齐 PC 版 final-cta） */}
            <footer className='final-cta' aria-label='产品咨询与联合发布'>
                <div className='final-contact'>
                    <figure className='wechat-contact'>
                        <img src={wechatConsultQr} alt='产品咨询微信二维码' />
                    </figure>
                    <div className='contact-copy'>
                        <small>产品咨询</small>
                        <a href={`tel:${CONSULT_PHONE}`}>{CONSULT_PHONE_SPACED_TEXT}</a>
                    </div>
                </div>
                <div className='partner-block'>
                    <div className='final-partners' aria-label='数文科技与浙江省人工智能与文化遗产国际科技合作基地'>
                        <img className='shuwen-logo' src={shuwenLogo} alt='数文科技' />
                        <span aria-hidden='true' />
                        <img className='hcu-logo' src={heritageLogo} alt='浙江省人工智能与文化遗产国际科技合作基地' />
                    </div>
                    <small>联合发布</small>
                </div>
                <button type='button' className='back-top' onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                    返回顶部
                    <Icon type='arrow' className='back-top-icon' />
                </button>
            </footer>

            {/* 产品咨询弹框：服务电话 + 复制号码（点遮罩关闭） */}
            {consultOpen && (
                <div className='consult-mask' onClick={() => setConsultOpen(false)}>
                    <div className='consult-dialog' role='dialog' aria-modal='true' aria-labelledby='consult-dialog-title' onClick={(event) => event.stopPropagation()}>
                        <div className='consult-panel'>
                            <p className='consult-label' id='consult-dialog-title'>服务电话</p>
                            <p className='consult-phone'>{CONSULT_PHONE_TEXT}</p>
                            <p className='consult-time'>{CONSULT_WORK_TIME}</p>
                        </div>
                        <div className='consult-actions'>
                            <button type='button' className='consult-copy' onClick={onCopyPhone}>复制号码</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    </>);
};
